import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { AlertTriangle, Download, MapPin } from "lucide-react";
import { fmtDateTime, companyTz } from "@/lib/tz";
import { useCompanyTz } from "@/context/CompanyTzContext";

export default function AdminTimeEntries() {
  const { tz } = useCompanyTz();   // re-renders the table once the zone is known
  const [rows, setRows] = useState([]);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  useEffect(() => {
    (async () => {
      const url = onlyFlagged ? "/time-entries?flagged=true" : "/time-entries";
      const { data } = await api.get(url);
      setRows(data);
    })();
  }, [onlyFlagged]);

  const flaggedCount = useMemo(() => rows.filter((r) => r.flagged).length, [rows]);
  const totalHours = useMemo(() => rows.reduce((a, b) => a + (b.duration_hours || 0), 0), [rows]);

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">§ TIME ENTRIES</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Time Tracking</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-1.5">
            <input data-testid="only-flagged" type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} /> Only Flagged
          </label>
          <a data-testid="download-time-csv" href={`${API}/reports/time-entries.csv`} target="_blank" rel="noreferrer" className="border-2 border-ink px-4 py-1.5 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-secondary">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card label="Total Entries" val={rows.length} />
        <Card label="Flagged" val={flaggedCount} color="text-bad" />
        <Card label="Total Hours" val={totalHours.toFixed(2)} money={false} suffix="h" />
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="time-entries-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Employee</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Worksite</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Clock In ({companyTz()})</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Clock Out</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Duration</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Distance</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No entries.</td></tr>}
            {rows.map((e, i) => (
              <tr key={e.id} className={`${i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} ${e.flagged ? "bg-bad/5" : ""}`} data-testid={`admin-time-row-${e.id}`}>
                <td className="px-3 py-3 border-t border-ink/20 font-semibold">{e.employee_name}</td>
                <td className="px-3 py-3 border-t border-ink/20">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.worksite_name || "—"}</span>
                </td>
                <td className="px-3 py-3 border-t border-ink/20">{e.clock_in_at ? fmtDateTime(e.clock_in_at) : "—"}</td>
                <td className="px-3 py-3 border-t border-ink/20">{e.clock_out_at ? fmtDateTime(e.clock_out_at) : <span className="text-good font-bold">Active</span>}</td>
                <td className="px-3 py-3 border-t border-ink/20 text-right">{(e.duration_hours || 0).toFixed(2)}h</td>
                <td className="px-3 py-3 border-t border-ink/20 text-right">{e.clock_in_distance_m != null ? `${Math.round(e.clock_in_distance_m)}m` : "—"}</td>
                <td className="px-3 py-3 border-t border-ink/20">
                  {e.flagged ? (
                    <div className="flex items-start gap-1.5">
                      <AlertTriangle className="w-4 h-4 text-bad shrink-0 mt-0.5" />
                      <div className="text-[11px] text-bad leading-tight">{e.flag_reason}</div>
                    </div>
                  ) : (
                    <span className="px-2 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest">OK</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, val, color = "", money = false, suffix = "" }) {
  return (
    <div className="hard-card p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-mono font-black text-3xl ${color}`}>{val}{suffix}</div>
    </div>
  );
}
